import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import QuizAnswerMode from './QuizAnswerMode';
import QuizReviewMode from './QuizReviewMode';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { enrichQuestionsWithDbFields, mapDbQuestionsToStandard } from '../../utils/questionMapper';
import { getQuestionsCorrectRate, calcDifficultyByCorrectRate, type QuestionCorrectRateInfo } from '../../utils/questionStats';
import { categoryStore } from '../../store/categoryStore';
import type { Question, Paper, QuizProps } from './types';

export { type QuizProps } from './types';

export default function Quiz(props?: QuizProps) {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 模式判断
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const isEditorPreview = searchParams.get('mode') === 'editor-preview' || location.state?.previewData?.isEditorPreview;
  const fromResult = location.state?.fromResult;
  const isProxyView = location.state?.isProxyView;
  const studentName = location.state?.studentName;
  const isPreview = isPreviewMode || isEditorPreview;

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [examQuestions, setExamQuestions] = useState<Question[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [customError, setCustomError] = useState<string>(''); // 自定义组卷错误信息
  const [reviewData, setReviewData] = useState<{
    answers: Record<string, string>;
    score: number;
    timeLeft: number;
    objectiveScore?: number | null;
    objectiveTotalScore?: number | null;
    programmingScore?: number | null;
    questionFeedbacks?: Record<string, { score?: number; feedback?: string }> | null;
    source?: 'practice' | 'class' | 'exam'; // 新增：来源字段
    correctRateMap?: Record<string, QuestionCorrectRateInfo>; // 新增：题目正确率数据
  } | null>(null);

  // 初始化数据加载
  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // 自定义组卷模式：根据 customConfig 自己查询抽题
      // 不再依赖 ExamPlatform 传递的 prebuiltQuestions（大数据通过 location.state 传递会丢失）
      const customConfig = (location.state as any)?.customConfig;
      console.log('[Quiz诊断] paperId:', paperId, 'customConfig存在:', !!customConfig, 'fromResult:', fromResult, 'isProxyView:', isProxyView, 'location.state存在:', !!location.state);

      // 回顾/代查模式优先：fromResult 和 isProxyView 不依赖 customConfig，
      // 必须在 customConfig 检查之前判断，否则自定义组卷的 paperId='custom' 会拦截回顾模式
      if (fromResult || isProxyView) {
        console.log('[Quiz诊断] 进入回顾/代查模式，跳过 customConfig 检查');
        // 以下逻辑由后面的 fromResult/isProxyView 分支处理
      } else if (!customConfig && (paperId === 'custom' || !paperId)) {
        // 关键修复：当 customConfig 不存在时（如浏览器刷新导致 location.state 丢失），
        // 直接给出明确错误提示，避免走普通试卷加载逻辑导致"未找到试卷数据"的误导
        // 不再依赖 paperId === 'custom' 判断，因为路由匹配可能让 paperId 不是 'custom'
        console.log('[Quiz诊断] customConfig丢失，显示过期提示');
        setCustomError('组卷数据已过期，请返回重新生成试卷');
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 只要 customConfig 存在就走自定义组卷逻辑，不依赖 paperId 判断
      if (customConfig) {
        console.log('[Quiz诊断] 进入自定义组卷逻辑, customConfig:', JSON.stringify(customConfig));
        let errorMsg = '';
        try {
          // 1. 获取当前登录用户
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user) {
            navigate('/login');
            return;
          }
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // 2. 根据 customConfig 查询并抽题
          let pickedAll: any[] = [];

          // 2.1 拉取所有已发布试卷
          const { data: papersData, error: papersError } = await supabase
            .from('papers')
            .select('id,organization,category_type,level_text,paper_type,is_free')
            .eq('is_published', true)
            .is('deleted_at', null);
          if (papersError) throw new Error(`查询试卷失败: ${papersError.message}`);
          console.log('[Quiz诊断] 查询到已发布试卷数:', (papersData || []).length);

          // 2.2 本地筛选符合条件的试卷
          const orgName = customConfig.orgName || '';
          const catName = customConfig.catName || '';
          const levelName = customConfig.levelName || '';
          const paperType = customConfig.paperType || 'all';

          await categoryStore.init();
          const matchedPaperIds: string[] = [];
          (papersData || []).forEach((p: any) => {
            if (orgName && p.organization !== orgName) return;
            const resolvedCat = categoryStore.getCategoryNameByCode(p.category_type) || p.category_type;
            if (catName && resolvedCat !== catName && p.category_type !== catName) return;
            if (levelName && p.level_text !== levelName) return;
            if (paperType === 'real' && p.paper_type !== 'real') return;
            if (paperType === 'mock' && p.paper_type !== 'mock') return;
            matchedPaperIds.push(p.id);
          });
          console.log('[Quiz诊断] 匹配试卷数:', matchedPaperIds.length, 'orgName:', orgName, 'catName:', catName, 'levelName:', levelName);

          if (matchedPaperIds.length === 0) {
            errorMsg = '未找到符合条件的试卷，请调整筛选条件后重试';
            throw new Error(errorMsg);
          }

          // 2.3 拉取题目
          const allQuestions: any[] = [];
          const BATCH = 50;
          for (let i = 0; i < matchedPaperIds.length; i += BATCH) {
            const batch = matchedPaperIds.slice(i, i + BATCH);
            const { data: qs, error: qErr } = await supabase
              .from('questions')
              .select('*')
              .in('paper_id', batch);
            if (qErr) console.warn('查询题目批次失败:', qErr.message);
            if (qs) allQuestions.push(...qs);
          }
          console.log('[Quiz诊断] 查询到题目数:', allQuestions.length);

          if (allQuestions.length === 0) {
            errorMsg = '匹配的试卷中暂无题目';
            throw new Error(errorMsg);
          }

          // 2.4 按题型分类
          const singlePool = allQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'single' || t === 'single_choice';
          });
          const judgePool = allQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'judge';
          });
          const programPool = allQuestions.filter(q => {
            const t = q.question_type || q.type;
            return t === 'code' || t === 'subjective' || t === 'python_coding' || t === 'scratch_coding' || t === 'cpp_coding';
          });
          console.log('[Quiz诊断] 题型分类: 单选', singlePool.length, '判断', judgePool.length, '编程', programPool.length);

          // 2.5 拉取正确率并按难度筛选
          const allQuestionIds = allQuestions.map(q => q.id);
          const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);

          const difficultyFilterMap: Record<string, string[]> = {
            '简单题': ['easy'], '中等题': ['medium'], '难题': ['hard'], '均衡': ['easy', 'medium', 'hard']
          };
          const targetDifficulties = difficultyFilterMap[customConfig.difficulty] || ['easy', 'medium', 'hard'];

          const filterByDifficulty = (pool: any[]) => {
            return pool.filter(q => {
              const correctRate = correctRateMap[q.id]?.rate;
              const diff = calcDifficultyByCorrectRate(correctRate);
              return targetDifficulties.includes(diff);
            });
          };

          const shuffle = <T,>(arr: T[]): T[] => {
            const a = [...arr];
            for (let i = a.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [a[i], a[j]] = [a[j], a[i]];
            }
            return a;
          };

          const needSingle = customConfig.singleChoiceCount || 0;
          const needJudge = customConfig.judgeCount || 0;
          const needProgram = customConfig.programCount || 0;
          const isBalanced = customConfig.difficulty === '均衡';

          const groupByDiff = (pool: any[]) => {
            const groups: Record<'easy' | 'medium' | 'hard', any[]> = { easy: [], medium: [], hard: [] };
            pool.forEach(q => {
              const correctRate = correctRateMap[q.id]?.rate;
              const diff = calcDifficultyByCorrectRate(correctRate);
              groups[diff].push(q);
            });
            return groups;
          };

          const pickByRatio = (pool: any[], total: number): any[] => {
            if (total === 0) return [];
            const groups = groupByDiff(pool);
            const easyNeed = Math.floor(total * 0.3);
            const mediumNeed = Math.floor(total * 0.5);
            const hardNeed = total - easyNeed - mediumNeed;

            const picked: any[] = [];
            picked.push(...shuffle(groups.easy).slice(0, easyNeed));
            picked.push(...shuffle(groups.medium).slice(0, mediumNeed));
            picked.push(...shuffle(groups.hard).slice(0, hardNeed));

            const deficit = total - picked.length;
            if (deficit > 0) {
              const pickedIds = new Set(picked.map(q => q.id));
              const remaining = [
                ...groups.easy.filter(q => !pickedIds.has(q.id)),
                ...groups.medium.filter(q => !pickedIds.has(q.id)),
                ...groups.hard.filter(q => !pickedIds.has(q.id)),
              ];
              picked.push(...shuffle(remaining).slice(0, deficit));
            }
            return picked;
          };

          // 降级策略：难度筛选后题目池为空时，从原始池随机抽取
          // 核心原则：只要原始池有足够题目，组卷就必须成功
          const hasRateData = Object.keys(correctRateMap).length > 0;

          const pickWithFallback = (pool: any[], count: number): any[] => {
            if (count === 0) return [];
            if (!hasRateData) return shuffle(pool).slice(0, count);

            if (isBalanced) {
              const picked = pickByRatio(pool, count);
              if (picked.length >= count) return picked;
              // 补足差额
              const deficit = count - picked.length;
              const pickedIds = new Set(picked.map(q => q.id));
              const remaining = pool.filter(q => !pickedIds.has(q.id));
              picked.push(...shuffle(remaining).slice(0, deficit));
              return picked;
            } else {
              const filtered = filterByDifficulty(pool);
              if (filtered.length > 0) return shuffle(filtered).slice(0, count);
              // 降级：难度筛选后为空，从原始池随机抽取
              console.warn(`难度筛选后题目池为空（目标难度：${targetDifficulties.join('/')}），降级为从原始池随机抽取`);
              return shuffle(pool).slice(0, count);
            }
          };

          const pickedSingle = pickWithFallback(singlePool, needSingle);
          const pickedJudge = pickWithFallback(judgePool, needJudge);
          const pickedProgram = pickWithFallback(programPool, needProgram);
          pickedAll = [...pickedSingle, ...pickedJudge, ...pickedProgram];
          console.log('[Quiz诊断] 抽题结果: 单选', pickedSingle.length, '判断', pickedJudge.length, '编程', pickedProgram.length, '总计', pickedAll.length);

          if (pickedAll.length === 0) {
            errorMsg = '当前筛选条件下题量不足，请调整筛选条件后重试';
            throw new Error(errorMsg);
          }

          // 3. 映射为标准格式并组装试卷
          const mappedQuestions = mapDbQuestionsToStandard(pickedAll);
          const totalScore = mappedQuestions.reduce((sum, q) => sum + (q.score || 0), 0);
          console.log('[Quiz诊断] 映射完成, 题目数:', mappedQuestions.length, '总分:', totalScore);

          setPaper({
            id: 'custom',
            title: `${customConfig.orgName || '全部'}-${customConfig.catName || '全部'}-${customConfig.levelName || '全部'} 自定义组卷`,
            questions: mappedQuestions,
            total_score: totalScore,
            organization: customConfig.orgName || '',
            category_type: customConfig.catName || '',
            level_text: customConfig.levelName || '',
          } as any);
          setExamQuestions(mappedQuestions);
          setHasPermission(true);
          setLoading(false);
          console.log('[Quiz诊断] 组卷成功完成');
          return;
        } catch (err: any) {
          console.error('[Quiz诊断] 自定义组卷加载失败:', err);
          setCustomError(err?.message || '自定义组卷加载失败，请稍后重试');
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 预览模式：加载试卷数据
      if (isPreviewMode || isEditorPreview) {
        // 从 previewData 获取数据（编辑器预览）
        const previewData = location.state?.previewData;
        if (previewData && previewData.questions) {
          setPaper({
            id: previewData.paperId,
            title: previewData.paperTitle,
            questions: previewData.questions,
            total_score: previewData.totalScore,
            category_type: ''
          });
          setExamQuestions(previewData.questions);
        } else if (paperId) {
          // 从数据库加载（试卷列表/草稿列表跳转）
          // 先尝试从 papers 表查询
          let { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
          let isTeacherPaper = false;

          // 如果找不到，尝试从 teacher_papers 表查询
          if (!paperData) {
            const { data: teacherPaperData } = await supabase.from('teacher_papers').select('*').eq('id', paperId).single();
            if (teacherPaperData) {
              paperData = teacherPaperData;
              isTeacherPaper = true;
            }
          }

          if (paperData) {
            const questionsTable = isTeacherPaper ? 'teacher_questions' : 'questions';
            const { data: questionsData } = await supabase.from(questionsTable).select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
            const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
            setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
            setExamQuestions(mappedQuestions);
          }
        }
        setHasPermission(true);
        setLoading(false);
        return;
      }

      // 代查模式（管理员/教师查看学员答题记录）
      if (isProxyView) {
        // 获取当前登录用户（教师）信息
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });
        }

        const resultData = location.state?.resultData;
        if (resultData && resultData.questions && resultData.questions.length > 0) {
          // 从数据库补充布局/缩放字段
          const { data: questionsData } = await supabase
            .from('questions')
            .select('id, content, title, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
            .eq('paper_id', resultData.paperId);

          let enrichedQuestions = resultData.questions;
          if (questionsData) {
            enrichedQuestions = enrichQuestionsWithDbFields(resultData.questions, questionsData);
          }

          setPaper({
            id: resultData.paperId,
            title: resultData.paperTitle,
            questions: enrichedQuestions,
            total_score: resultData.totalScore,
            category_type: ''
          });
          setExamQuestions(enrichedQuestions);
          // 加载题目正确率数据
          const allQuestionIds = enrichedQuestions.map(q => q.id);
          const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);

          setReviewData({
            answers: resultData.answers || {},
            score: resultData.score,
            timeLeft: resultData.timeLeft,
            objectiveScore: resultData.objectiveScore,
            objectiveTotalScore: resultData.objectiveTotalScore,
            programmingScore: resultData.programmingScore,
            questionFeedbacks: resultData.questionFeedbacks || null,
            source: (resultData as any).source, // 新增：传递来源字段
            correctRateMap // 新增：传递正确率数据
          });
          setHasPermission(true);
          setLoading(false);
          return;
        }
      }

      // 回顾模式（从答题结果页跳转）
      if (fromResult) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
          setCurrentUser({ id: session.user.id, ...profile });

          // 优先使用 location.state 传递的 resultData（ExamResult 页面已补全传递）
          // 对于自定义组卷（paperId='custom'），Edge Function 无法按 paper_id 查找记录，
          // 所以必须依赖 resultData
          const stateResultData = (location.state as any)?.resultData;
          if (stateResultData && stateResultData.questions && stateResultData.questions.length > 0) {
            // 尝试从数据库补充布局/缩放字段（仅当 paperId 是真实 UUID 时才查询）
            let enrichedQuestions = stateResultData.questions;
            if (paperId && paperId !== 'custom' && /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(paperId)) {
              const { data: questionsData } = await supabase
                .from('questions')
                .select('id, content, title, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                .eq('paper_id', paperId);
              if (questionsData) {
                enrichedQuestions = enrichQuestionsWithDbFields(stateResultData.questions, questionsData);
              }
            }

            setPaper({
              id: stateResultData.paperId || paperId,
              title: stateResultData.paperTitle || '自定义组卷',
              questions: enrichedQuestions,
              total_score: stateResultData.totalScore || 0,
              category_type: ''
            });
            setExamQuestions(enrichedQuestions);
            // 加载题目正确率数据
            const allQuestionIds = enrichedQuestions.map(q => q.id);
            const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);

            setReviewData({
              answers: stateResultData.answers || {},
              score: stateResultData.score,
              timeLeft: stateResultData.timeLeft,
              objectiveScore: stateResultData.objectiveScore,
              objectiveTotalScore: stateResultData.objectiveTotalScore,
              programmingScore: stateResultData.programmingScore,
              questionFeedbacks: stateResultData.questionFeedbacks || null,
              source: (stateResultData as any).source,
              correctRateMap
            });
            setHasPermission(true);
            setLoading(false);
            return;
          }

          // 如果 resultData 不可用，尝试从 Edge Function 获取（仅适用于真实试卷 paperId）
          if (paperId && paperId !== 'custom') {
            const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ action: 'get_records' }),
            });

            const result = await response.json();

            if (result.success && result.records && result.records.length > 0) {
              const currentRecord = result.records.find((r: any) => r.paper_id === paperId);

              if (currentRecord && currentRecord.questions) {
                // 从数据库补充布局/缩放字段
                const { data: questionsData } = await supabase
                  .from('questions')
                  .select('id, content, title, title_image_layout, title_image_widths, title_images, image_position, option_image_widths, option_layout')
                  .eq('paper_id', paperId);

                let enrichedQuestions = currentRecord.questions;
                if (questionsData) {
                  enrichedQuestions = enrichQuestionsWithDbFields(currentRecord.questions, questionsData);
                }

                setPaper({
                  id: currentRecord.paper_id,
                  title: currentRecord.paper_title,
                  questions: enrichedQuestions,
                  total_score: currentRecord.total_score,
                  category_type: ''
                });
                setExamQuestions(enrichedQuestions);
                // 加载题目正确率数据
                const allQuestionIds = enrichedQuestions.map(q => q.id);
                const correctRateMap = await getQuestionsCorrectRate(allQuestionIds);

                setReviewData({
                  answers: currentRecord.answers || {},
                  score: currentRecord.score,
                  timeLeft: currentRecord.time_left,
                  objectiveScore: currentRecord.objectiveScore ?? currentRecord.objective_score,
                  objectiveTotalScore: currentRecord.objectiveTotalScore ?? currentRecord.objective_total_score,
                  programmingScore: currentRecord.programmingScore ?? currentRecord.programming_score,
                  questionFeedbacks: currentRecord.question_feedbacks || null,
                  source: currentRecord.source,
                  correctRateMap
                });
                setHasPermission(true);
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      // 收藏练习模式已改为页面内切换，不再使用独立路由
      // 如果有人通过旧链接访问，重定向到收藏页面
      if (paperId === 'favorites-practice') {
        navigate('/favorites', { replace: true });
        return;
      }

      // 正常答题模式：检查登录和权限
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      setCurrentUser({ id: session.user.id, ...profile });

      // 加载试卷数据
      if (paperId) {
        const assignmentId = searchParams.get('assignmentId');

        // 如果有 assignmentId，通过 Edge Function 获取试卷和题目数据（绕过 RLS）
        if (assignmentId && session) {
          try {
            const assignmentResponse = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ action: 'start_assignment', assignmentId }),
            });
            const assignmentResult = await assignmentResponse.json();

            if (assignmentResult.success && assignmentResult.data?.paper && assignmentResult.data?.questions) {
              const paperData = assignmentResult.data.paper;
              const questionsData = assignmentResult.data.questions;
              const enabledTypes = assignmentResult.data.enabledQuestionTypes;

              const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
              let filteredQuestions = mappedQuestions;
              let effectiveTotalScore = paperData.total_score || 0;

              if (enabledTypes) {
                filteredQuestions = mappedQuestions.filter(q => enabledTypes.includes(q.type));
                effectiveTotalScore = filteredQuestions.reduce((sum, q) => sum + q.score, 0);
              }

              setPaper({ ...paperData, questions: filteredQuestions, total_score: effectiveTotalScore });
              setExamQuestions(filteredQuestions);
              setHasPermission(true);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('通过 Edge Function 获取作业数据失败:', err);
            // 失败时回退到直接查询数据库
          }
        }

        // 无 assignmentId 或 Edge Function 失败时，直接从数据库查询
        // 先尝试从 papers 表查询（平台试卷）
        let { data: paperData } = await supabase.from('papers').select('*').eq('id', paperId).single();
        if (!paperData) {
          // papers 表中找不到，尝试从 teacher_papers 表查询（教师自建试卷）
          const { data: teacherPaperData } = await supabase.from('teacher_papers').select('*').eq('id', paperId).single();
          if (teacherPaperData) {
            paperData = teacherPaperData;
            const { data: questionsData } = await supabase.from('teacher_questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });
            const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
            setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
            setExamQuestions(mappedQuestions);
          }
        } else if (paperData) {
          const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paperId).order('order_index', { ascending: true });

          const mappedQuestions = mapDbQuestionsToStandard(questionsData || []);
          setPaper({ ...(paperData as any), questions: mappedQuestions, total_score: (paperData as any).total_score || 0 });
          setExamQuestions(mappedQuestions);
        }
      }

      setHasPermission(true);
      setLoading(false);
    };

    init();
  }, [navigate, paperId, location.state, isPreviewMode, isEditorPreview, fromResult, isProxyView]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 text-sm font-medium">正在加载题目，请稍候...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">暂无权限</h2>
          <p className="text-gray-600 mb-6">您没有权限参加此考试，请先购买相应套餐。</p>
          <button onClick={() => navigate('/membership')} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            去购买套餐
          </button>
        </div>
      </div>
    );
  }

  // 预览模式 - 使用 QuizAnswerMode (isPreview=true)
  if (isPreview) {
    if (!paper || examQuestions.length === 0) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">试卷数据加载中...</div>
        </div>
      );
    }
    return (
      <QuizAnswerMode
        paper={paper}
        examQuestions={examQuestions}
        currentUser={currentUser}
        paperId={paperId}
        isPreview={true}
        isEditorPreview={isEditorPreview}
        onBack={handleBack}
      />
    );
  }

  // 回顾模式（从结果页查看或代查模式）
  if (fromResult || isProxyView) {
    if (!paper || !reviewData) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">数据加载失败</div>
        </div>
      );
    }
    return (
      <QuizReviewMode
        paper={paper}
        examQuestions={examQuestions}
        answers={reviewData.answers}
        score={reviewData.score}
        timeLeft={reviewData.timeLeft}
        objectiveScore={reviewData.objectiveScore}
        objectiveTotalScore={reviewData.objectiveTotalScore}
        programmingScore={reviewData.programmingScore}
        questionFeedbacks={reviewData.questionFeedbacks}
        currentUser={currentUser}
        paperId={paperId}
        studentName={studentName}
        source={reviewData.source}
        correctRateMap={reviewData.correctRateMap}
        onBack={handleBack}
      />
    );
  }

  // 正常答题模式
  if (!paper || examQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">试卷加载失败</h2>
          <p className="text-gray-600 mb-6 text-sm">
            {customError || '未找到试卷数据，请返回重试或调整筛选条件'}
          </p>
          <button
            onClick={() => navigate('/exam-platform')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            返回组卷
          </button>
        </div>
      </div>
    );
  }

  return (
    <QuizAnswerMode
      paper={paper}
      examQuestions={examQuestions}
      currentUser={currentUser}
      paperId={paperId}
      isPreview={false}
      isEditorPreview={false}
      onBack={handleBack}
    />
  );
}
